import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {bulkScope,refreshBulkCampaigns} from './bulk-context';
import { K } from '../kabinet/atoms';
import { ВГруппу } from './gruppy';
import { целые } from '../ui/chisla';

/**
 * Полоса массовых действий над выделенными кампаниями.
 *
 * ПОЯВЛЯЕТСЯ ТОЛЬКО КОГДА ЕСТЬ ВЫДЕЛЕНИЕ. Постоянная панель с кнопками,
 * которые почти всегда ничего не делают, приучает их не замечать —
 * а замечать их надо ровно в тот момент, когда они опасны.
 *
 * ЦЕНУ РЕШЕНИЯ СЧИТАЕТ СЕРВЕР, а не эта полоса. «Остановить 36 из 36,
 * работать не останется ни одной» — фраза, которая останавливает промах
 * выделения, и собирается она из тех же чисел, по которым действие
 * и выполнится. Собери её здесь — и однажды экран пообещает одно,
 * а сервис сделает другое.
 *
 * ПОДТВЕРЖДЕНИЕ — ВТОРЫМ НАЖАТИЕМ НА ТУ ЖЕ КНОПКУ, а не отдельным окном.
 * Окно посреди списка закрывает то, что человек как раз и хочет
 * перепроверить: какие строки он выделил.
 */

interface Template {
  id: string;
  name: string;
  description: string;
}

type Action = 'pause' | 'start' | 'manage' | 'unmanage' | 'schedule';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СЛОВА И ИМЯ УЗЛА — ЭТАЛОННЫЕ (ответ К-104).
 *
 * Стояло «Снять управление», в эталоне — «Снять с управления»,
 * и предлог здесь не украшение: «снять управление» читается как
 * «снять управление С СЕРВИСА», то есть наоборот. Узлу дано имя
 * `кнопка-снять-с-управления` — дизайнер называет его именно так,
 * и сверка ходит по именам.
 *
 * ПАРНОЙ КНОПКИ «ПОД УПРАВЛЕНИЕ» У ЭТАЛОНА В ПОЛОСЕ НЕТ, а у нас есть
 * и остаётся: массово ВЕРНУТЬ под управление — это то же действие
 * в обратную сторону, и без него снятое возвращается по одной кампании.
 * ═══════════════════════════════════════════════════════════════════════
 */
const ACTIONS: Array<{ id: Action; title: string; danger?: boolean; имя?: string }> = [
  { id: 'pause', title: 'Пауза', danger: true },
  { id: 'start', title: 'Пуск' },
  { id: 'manage', title: 'Под управление' },
  { id: 'unmanage', title: 'Снять с управления', danger: true, имя: 'кнопка-снять-с-управления' },
];

export function BulkBar({
  base,
  ids,
  total,
  расход,
  onClear,
  onDone,
}: {
  base: string;
  ids: string[];
  total: number;
  /** Общий расход выделенных, ₽. `null` — считать не из чего. */
  расход: number | null;
  onClear: () => void;
  onDone: (text: string) => void;
}) {
  const qc = useQueryClient();
  const [confirmation, setConfirmation] = useState<{action:Action;scope:string}|null>(null);
  const [templateId, setTemplateId] = useState('');
  const scope = bulkScope(base,ids,total,templateId);
  const armed = confirmation?.scope === scope ? confirmation.action : null;
  useEffect(()=>setConfirmation(null),[scope]);
  const setArmed = (action:Action|null) => setConfirmation(action===null?null:{action,scope});

  const templates = useQuery({
    queryKey: ['schedule-templates', base],
    // Пока строки не выбраны, сама полоса скрыта и шаблоны не нужны.
    enabled: ids.length > 0,
    queryFn: async () =>
      (await api<{ templates: Template[] }>(`${base}/schedule-templates`)).templates,
  });

  const run = useMutation({
    mutationFn: (v: { action: Action; templateId?: string }) =>
      api<{ message: string; done: number; failed: number }>(`${base}/campaigns/bulk`, {
        method: 'POST',
        body: { ids, action: v.action, templateId: v.templateId },
      }),
    onSuccess: async (r) => {
      onDone(r.message);
      setArmed(null);
      onClear();
      // Плитки — вместе со списком: они считают то же самое.
      await refreshBulkCampaigns(qc);
    },
    onError: (e) => {
      onDone(e instanceof Error ? e.message : 'Не вышло');
      setArmed(null);
    },
  });

  if (ids.length === 0) return null;

  const press = (action: Action, tpl?: string) => {
    // ВТОРОЕ НАЖАТИЕ — ПОДТВЕРЖДЕНИЕ. Первое взводит кнопку и показывает,
    // что именно произойдёт; безопасные действия взводить незачем.
    const dangerous = ACTIONS.find((a) => a.id === action)?.danger || action === 'schedule';
    if (dangerous && armed !== action) {
      setArmed(action);
      return;
    }
    run.mutate({ action, templateId: tpl });
  };

  return (
    /* ═══════════════════════════════════════════════════════════════
       ПОЛОСА ПРИЖАТА К НИЗУ ОКНА И ТЁМНАЯ — как в эталоне.

       Здесь она была липкой шапкой поверх таблицы (`sticky top-0`)
       на светлой подложке марки. Разница не косметическая: липкая
       шапка закрывает первые строки списка — ровно те, которые
       человек только что выделил и хочет перепроверить, — а на
       светлом фоне полоса читается как ещё одна панель, которая
       была тут всегда.

       Тёмная полоса внизу читается как «сейчас действие уйдёт
       на эти строки». Нижнее поле экрана (96 пикселей в коробке)
       заведено ровно под неё: последняя строка таблицы под полосу
       не уходит.
       ═══════════════════════════════════════════════════════════ */
    <div data-d="полоса-выделенного" className={K.полосаВыделенного} role="region" aria-label="Выделенные кампании">
      <b className={K.счётВыделенного}>
        Выделено {ids.length}
        {total > 0 && <span style={{ fontWeight: 400, opacity: 0.7 }}> из {total}</span>}
      </b>

      {/* ОБЩИЙ РАСХОД ВЫДЕЛЕННОГО — рядом со счётом, как в эталоне.
          Число кампаний не говорит о цене решения: «остановить 8»
          — это восемь строк или восемьдесят тысяч рублей в месяц?
          Ответ здесь. */}
      {расход !== null && (
        <span className={K.расходВыделенного}>общий расход {деньги(расход)} ₽</span>
      )}

      <span className={K.кнопкиВыделенного}>
        {/* «В ГРУППУ» ПЕРВОЙ, как в эталоне: это единственное здесь
            действие, которое ничего не меняет в Wildberries, и делают
            его чаще всех остальных вместе взятых. */}
        <ВГруппу base={base} ids={ids} onDone={onDone} />

        {ACTIONS.map((a) => (
          <button
            key={a.id}
            type="button"
            data-d={a.имя}
            className={K.кнопкаВыделенного}
            disabled={run.isPending}
            onClick={() => press(a.id)}
            style={armed === a.id ? ВЗВЕДЕНА : undefined}
          >
            {armed === a.id ? `Точно? ${a.title}` : a.title}
          </button>
        ))}

        {/* РАСПИСАНИЕ — РАДИ ЧЕГО ШАБЛОНЫ И ЗАВОДИЛИСЬ. Разметить часы
            один раз и применить к тридцати кампаниям — это и есть
            работа, которую машина обязана делать за человека. */}
        {(templates.data ?? []).length > 0 && (
          <span className="flex items-center gap-1">
            <select
              className="rk-field"
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setArmed(null);
              }}
              style={{ maxWidth: 260, height: 30 }}
            >
              <option value="">расписание по шаблону…</option>
              {(templates.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.description}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={K.кнопкаВыделенного}
              disabled={templateId === '' || run.isPending}
              onClick={() => press('schedule', templateId)}
              style={armed === 'schedule' ? ВЗВЕДЕНА : undefined}
            >
              {armed === 'schedule' ? 'Точно? Применить' : 'Применить'}
            </button>
          </span>
        )}

        <button type="button" className={K.кнопкаСнятьВыделение} onClick={onClear}>
          Снять выделение
        </button>
      </span>

      {armed !== null && (
        <span
          style={{
            fontSize: 'var(--fs-sm)',
            lineHeight: 'var(--lh-sm)',
            opacity: 0.85,
            width: '100%',
          }}
        >
          нажмите ещё раз — действие затронет все выделенные кампании
        </span>
      )}

      {run.isPending && <span style={{ opacity: 0.7 }}>выполняю…</span>}
    </div>
  );
}

/**
 * Взведённая кнопка — светлой плашкой, а не красным текстом.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЛОСА ТЁМНАЯ, И КРАСНЫЕ ЧЕРНИЛА НА НЕЙ НЕ ЧИТАЮТСЯ.
 *
 * `--bad-ink` — тёмно-красный: он рассчитан на светлую подложку
 * и на чернильном фоне полосы сливается с ней. Поэтому взведённая
 * кнопка получает СВОЮ подложку (`--bad-wash`) и уже на ней —
 * положенные чернила. Это тот же приём, что у плашек отказа,
 * и он же держит правило «смысловой цвет не красит текст».
 * ═══════════════════════════════════════════════════════════════════════
 */
const ВЗВЕДЕНА = {
  background: 'var(--bad-wash)',
  color: 'var(--bad-ink)',
  borderColor: 'var(--bad)',
  fontWeight: 600,
} as const;

/** Рубли без копеек, с неразрывными пробелами — как везде в кабинете. */
function деньги(v: number): string {
  return целые.format(v).replace(/ /g, '\u00a0');
}
